import { PlusOutlined } from '@ant-design/icons';
import { Button, message } from 'antd';
import { AccessControl } from 'components/AccessControl';
import {
  DELETE_ERROR,
  FETCH_ERROR,
  REQUEST_LOADING,
  SUCCESS_DELETE,
} from 'constants/alerts';
import { useDocumentTitle } from 'hooks/useDocumentTitle';
import { useDrawers } from 'hooks/useDrawers';
import { useFilters } from 'hooks/useFilters';
import { useHeaderChanger } from 'hooks/useHeaderChanger';
import { usePagination } from 'hooks/usePagination';
import { useEffect, useState } from 'react';
import { SegmentsService } from 'services/SegmentsService';
import { ActionsWrapper } from 'ui/ActionsWrapper';
import { SegmentDetail } from './SegmentDetail';
import { SegmentsForm } from './SegmentsForm';
import { SegmentsTable } from './SegmentsTable';

const PAGE_TITLE = 'Сегмент';

function Segments() {
  useDocumentTitle(PAGE_TITLE);
  const { setCount } = useHeaderChanger(PAGE_TITLE);
  const [segments, setSegments] = useState([]);
  const [loading, setLoading] = useState(false);
  const {
    state: { drawerVisible, formIsActive, activeRecord },
    openDrawer,
    closeDrawer,
    openCreateForm,
    openEditForm,
    closeForm,
  } = useDrawers();
  const {
    page,
    totalRows,
    perPage,
    setTotalRows,
    setPagination,
    resetPagination,
  } = usePagination();
  const { filters, handleFilters } = useFilters({
    start_date: null,
    end_date: null,
  });

  useEffect(() => {
    fetchSegments();
  }, [page, perPage, filters]);

  const fetchSegments = async () => {
    if (!page || !perPage) {
      return;
    }

    setLoading(true);

    try {
      const { items, total } = await SegmentsService.getSegments(
        page,
        perPage,
        filters
      );

      setSegments(items);
      setCount(total);
      setTotalRows(total);
    } catch (err) {
      message.error(FETCH_ERROR);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    const hide = message.loading(REQUEST_LOADING);

    try {
      const isDeleted = await SegmentsService.deleteSegment(activeRecord.id);

      if (isDeleted) {
        closeDrawer();
        fetchSegments();
        message.success(SUCCESS_DELETE);
      }
    } catch (err) {
      message.error(DELETE_ERROR);
    } finally {
      hide();
    }
  };

  return (
    <>
      <ActionsWrapper placeEnd>
        <AccessControl pageId="segments" action="create">
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={openCreateForm}
          >
            Создать
          </Button>
        </AccessControl>
      </ActionsWrapper>

      <SegmentDetail
        activeRecord={activeRecord}
        isActive={drawerVisible}
        onClose={closeDrawer}
        onEdit={openEditForm}
        onDelete={handleDelete}
      />

      <SegmentsTable
        items={segments}
        loading={loading}
        current={page}
        pageSize={perPage}
        totalRows={totalRows}
        filters={filters}
        onRowClick={openDrawer}
        onChange={setPagination}
        onFilter={(value) => {
          handleFilters(value);
          resetPagination();
        }}
      />

      <SegmentsForm
        initialValues={activeRecord}
        isActive={formIsActive}
        onClose={closeForm}
        onSuccess={fetchSegments}
      />
    </>
  );
}

export { Segments };
